import React from 'react';
import { useNavigate } from 'react-router-dom';

import { LandingChevronRight } from 'assets/svg/landing/landingIcons';
import ExecutiveSummaryCard, { CARD_SHELL_CLASSNAME } from 'components/molecules/Landing/ExecutiveSummaryCard';
import { messageConfig } from 'config/appMessageConfig';
import {
    RGM_HOME_EXEC_SUMMARY,
    RGM_HOME_EXEC_SUMMARY_ERROR,
    RGM_HOME_EXEC_SUMMARY_SKELETON,
    RGM_HOME_EXEC_SUMMARY_VIEW_MORE,
} from 'constants/landingTestIds';
import useExecutiveSummary from 'hooks/landing/useExecutiveSummary';
import { trackEventMixpanel } from 'utils/mixpanel';
import { LANDING_EXEC_SUMMARY_VIEW_MORE } from 'utils/mixpanelEventNames';

const SKELETON_KEYS = ['a', 'b', 'c', 'd'];

const LoadingRow = () => (
    <div data-testid={RGM_HOME_EXEC_SUMMARY_SKELETON} className="flex w-full items-center gap-4">
        {SKELETON_KEYS.map((key) => (
            <div key={key} className={`${CARD_SHELL_CLASSNAME} animate-pulse gap-2`}>
                <div className="h-[22px] w-2/3 rounded bg-filterV2Border" />
                <div className="h-8 w-1/3 rounded bg-filterV2Border" />
                <div className="h-5 w-1/2 rounded bg-filterV2Border" />
            </div>
        ))}
    </div>
);

/** Four KPI cards plus the link into the full Executive Summary screen. */
function ExecutiveSummarySection() {
    const navigate = useNavigate();
    const { cards, detailRoute, isLoading, isError } = useExecutiveSummary();

    const goToDetail = () => {
        trackEventMixpanel(LANDING_EXEC_SUMMARY_VIEW_MORE);
        navigate(detailRoute);
    };

    return (
        <section data-testid={RGM_HOME_EXEC_SUMMARY} className="flex w-full flex-col gap-3">
            <div className="flex w-full items-center justify-between px-4">
                <p className="font-sm font-medium leading-[1.2] text-blackTitle">EXECUTIVE SUMMARY</p>
                <button
                    type="button"
                    data-testid={RGM_HOME_EXEC_SUMMARY_VIEW_MORE}
                    onClick={goToDetail}
                    className="flex h-6 items-center rounded border border-filterV2Primary px-3 font-xxs font-normal leading-[18px] text-filterV2Primary"
                >
                    View more
                    <span className="flex size-5 shrink-0 items-center justify-center">
                        <LandingChevronRight className="size-[15px]" />
                    </span>
                </button>
            </div>

            {isLoading && <LoadingRow />}

            {!isLoading && (isError || cards.length === 0) && (
                <div data-testid={RGM_HOME_EXEC_SUMMARY_ERROR} className={`${CARD_SHELL_CLASSNAME} items-center`}>
                    <p className="font-xs text-filterV2TextSecondary">
                        {isError ? messageConfig.errorMessages.default : messageConfig.errorMessages['No Data']}
                    </p>
                </div>
            )}

            {!isLoading && !isError && cards.length > 0 && (
                <div className="flex w-full items-stretch gap-4">
                    {cards.map((card) => (
                        <ExecutiveSummaryCard key={card.id} card={card} />
                    ))}
                </div>
            )}
        </section>
    );
}

export default ExecutiveSummarySection;
